import { DeleteIcon, Modal, theme, useToast } from '@arnold/common';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled/macro';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteSurveyAttachmentMutation,
  useSurveyAttachmentDownloadUrlLazyQuery,
  useSurveyAttachmentsQuery,
} from '../../generated/hooks';

interface Props {
  surveyId: string;
  uploading: boolean;
}

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.e};
  margin: 0 0 ${theme.spacing.f} 0;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.d};
  padding: 6px 12px;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: 999px;
  background: ${theme.colors.backgroundCover.default};
  max-width: 100%;
`;

const FileNameButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;

  &:hover {
    text-decoration: underline;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  & svg {
    width: 16px;
    height: 16px;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid ${theme.colors.borderMain.default};
  border-top-color: ${theme.colors.actionPrimary.default};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SurveyAttachments: FC<Props> = ({ surveyId, uploading }) => {
  const [t] = useTranslation('survey');
  const { addToast } = useToast();
  const [attachmentToDelete, setAttachmentToDelete] = useState<{ id: string; name: string } | null>(null);

  const { data } = useSurveyAttachmentsQuery({
    variables: { surveyId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const [fetchDownloadUrl] = useSurveyAttachmentDownloadUrlLazyQuery({ fetchPolicy: 'network-only' });
  const [deleteAttachment, { loading: deleting }] = useDeleteSurveyAttachmentMutation();

  const attachments = data?.survey?.attachments ?? [];

  const handleDownload = async (attachmentId: string) => {
    try {
      const result = await fetchDownloadUrl({ variables: { attachmentId } });
      const url = result.data?.surveyAttachmentDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        addToast(t('attachmentDownloadError'));
      }
    } catch {
      addToast(t('attachmentDownloadError'));
    }
  };

  const handleDelete = async () => {
    if (!attachmentToDelete) {
      return;
    }
    try {
      const result = await deleteAttachment({
        variables: { attachmentId: attachmentToDelete.id },
        refetchQueries: ['surveyAttachments'],
        awaitRefetchQueries: true,
      });
      if (result.data?.deleteSurveyAttachment) {
        addToast(t('attachmentDeleteSuccess'));
      } else {
        addToast(t('attachmentDeleteError'));
      }
    } catch {
      addToast(t('attachmentDeleteError'));
    } finally {
      setAttachmentToDelete(null);
    }
  };

  if (attachments.length === 0 && !uploading) {
    return null;
  }

  return (
    <>
      <Chips data-icom="survey-attachments">
        {attachments.map((attachment) => (
          <Chip key={attachment.id}>
            <FileNameButton
              onClick={() => handleDownload(attachment.id)}
              title={attachment.originalName}
              data-icom="survey-attachment-download"
            >
              {attachment.originalName}
            </FileNameButton>
            <IconButton
              onClick={() => setAttachmentToDelete({ id: attachment.id, name: attachment.originalName })}
              aria-label={t('attachmentDeleteButton')}
              data-icom="survey-attachment-delete"
            >
              <DeleteIcon />
            </IconButton>
          </Chip>
        ))}
        {uploading && (
          <Chip data-icom="survey-attachment-uploading">
            <Spinner />
          </Chip>
        )}
      </Chips>

      <Modal
        show={!!attachmentToDelete}
        onHide={() => setAttachmentToDelete(null)}
        onSubmit={handleDelete}
        title={t('attachmentDeleteTitle')}
        text={t('attachmentDeleteConfirm', { name: attachmentToDelete?.name ?? '' })}
        buttons={{
          submit: { title: t('attachmentDeleteButton'), disabled: deleting },
          cancel: { title: t('attachmentBackButton') },
        }}
      />
    </>
  );
};

export default SurveyAttachments;
